import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  deliveriesApi,
  attachableKinds,
  DELIVERY_DOC_ALLOWED,
  DELIVERY_DOC_MAX_BYTES,
  DELIVERY_DOC_MAX_FILES,
  type DeliveryDocument,
  type DeliveryDocumentKind,
} from "../api/deliveries";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import { Select } from "./Select";
import Ic from "./icons.jsx";

/**
 * Papers attached to a delivery.
 *
 * The kind decides who may attach it, so the picker offers only the kinds this
 * user's role can actually file — the server refuses the rest, and offering a
 * button that returns 403 is worse than not offering it.
 *
 * A document may exist without a file: a certificate of analysis that arrived by
 * post is recorded by its number. That is a first-class case here, not a
 * workaround, so it has its own button.
 */
export function DeliveryDocuments({ deliveryId, deskMayAct }: { deliveryId: string; deskMayAct: boolean }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission, hasRole } = useAuth();

  const kinds = hasPermission("deliveries:documents") ? attachableKinds(hasRole, deskMayAct) : [];
  const [documents, setDocuments] = useState<DeliveryDocument[]>([]);
  const [kind, setKind] = useState<DeliveryDocumentKind>(kinds[0] ?? "OTHER");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setDocuments(await deliveriesApi.listDocuments(deliveryId));
    } catch {
      // The section is secondary to the delivery itself; a failure here must not
      // blank the page the user came for.
      setDocuments([]);
    }
  }, [deliveryId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleFile(f: File) {
    if (!DELIVERY_DOC_ALLOWED.includes(f.type)) {
      toast.error(t("deliveries.document.badType", "File type not allowed."));
      return;
    }
    if (f.size > DELIVERY_DOC_MAX_BYTES) {
      toast.error(t("deliveries.document.tooLarge", "File exceeds 25 MB."));
      return;
    }
    if (documents.length >= DELIVERY_DOC_MAX_FILES) {
      toast.error(t("deliveries.document.quotaReached", "Max 10 documents per delivery."));
      return;
    }
    setBusy(true);
    try {
      const presigned = await deliveriesApi.presignDocument(deliveryId, {
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
        kind,
      });
      const res = await fetch(presigned.url, { method: "PUT", headers: { "Content-Type": f.type }, body: f });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      await deliveriesApi.confirmDocument(deliveryId, {
        objectKey: presigned.objectKey,
        fileName: f.name,
        contentType: f.type,
        sizeBytes: f.size,
        kind,
      });
      toast.success(t("deliveries.document.uploaded", "Document attached."));
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function submitReference() {
    if (!reference.trim()) return;
    setBusy(true);
    try {
      await deliveriesApi.recordDocumentReference(deliveryId, { kind, externalReference: reference.trim() });
      setReference("");
      toast.success(t("deliveries.document.referenceSaved", "Reference recorded."));
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function download(d: DeliveryDocument) {
    try {
      const { url } = await deliveriesApi.documentDownloadUrl(deliveryId, d.id);
      window.open(url, "_blank");
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function remove(d: DeliveryDocument) {
    const ok = await confirm({
      message: t("deliveries.document.confirmDelete", "Delete {{name}}?", {
        name: d.fileName ?? d.externalReference ?? "",
      }),
      danger: true,
    });
    if (!ok) return;
    try {
      await deliveriesApi.deleteDocument(deliveryId, d.id);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const fmtDate = (v: string) => new Date(v).toLocaleDateString(i18n.resolvedLanguage);

  return (
    <section style={card}>
      <h2 style={cardTitle}>{t("deliveries.document.title", "Documents")}</h2>

      {documents.length === 0 ? (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 13 }}>
          {t("deliveries.document.empty", "No documents yet.")}
        </p>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {documents.map((d) => (
            <li key={d.id} style={row}>
              {d.objectKey ? <Ic.pdf /> : <Ic.copy />}
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13, color: "var(--ink)" }}>
                  {d.fileName ?? d.externalReference}
                </span>
                <span style={{ fontSize: 11, color: "var(--ink-3)" }}>
                  {t(`deliveries.document.kind.${d.kind}`, d.kind)}
                  {d.objectKey ? "" : ` · ${t("deliveries.document.paperOnly", "on paper, no file")}`} ·{" "}
                  {fmtDate(d.createdAt)}
                </span>
              </span>
              {d.objectKey && (
                <button type="button" className="btn sm" onClick={() => void download(d)}>
                  {t("deliveries.document.download", "Download")}
                </button>
              )}
              {hasPermission("section:delivery_documents:full") && kinds.includes(d.kind) && (
                <button type="button" className="btn sm" onClick={() => void remove(d)}>
                  {t("deliveries.document.delete", "Delete")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {kinds.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Select<DeliveryDocumentKind>
              value={kind}
              onChange={setKind}
              ariaLabel={t("deliveries.document.kindLabel", "Document kind")}
              options={kinds.map((k) => ({ value: k, label: t(`deliveries.document.kind.${k}`, k) }))}
            />
            <input
              ref={fileInput}
              type="file"
              accept={DELIVERY_DOC_ALLOWED.join(",")}
              disabled={busy || documents.length >= DELIVERY_DOC_MAX_FILES}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="input"
              placeholder={t("deliveries.document.referencePlaceholder", "Document number, e.g. COA-2026-0042")}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <button
              type="button"
              className="btn sm"
              disabled={busy || !reference.trim()}
              onClick={() => void submitReference()}
            >
              {t("deliveries.document.recordReference", "Record reference without a file")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const card: CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, color: "var(--ink)" };
const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px",
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
};
